import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';

/** Duration of the sliding animation — kept in sync with the track's transition */
const SLIDE_MS = 500;

interface CoverSlideshowProps {
  /** Image URLs to cycle through, in display order (the cover comes first) */
  images: string[];
  alt: string;
  /** How long each image stays on screen before the track slides to the next one */
  intervalMs?: number;
  /** Notifies the parent which image is currently showing (e.g. to open the modal on it) */
  onIndexChange?: (index: number) => void;
}

/**
 * Sliding slideshow used as the hero cover of a location. The images sit side by
 * side on a track that translates by one viewport at a time. It advances on its own
 * and can be driven by hand (arrows, dots, swipe with the track following the
 * finger); autoplay pauses while the user is interacting and is skipped entirely
 * when the OS asks for reduced motion.
 */
export default function CoverSlideshow({
  images,
  alt,
  intervalMs = 5000,
  onIndexChange,
}: CoverSlideshowProps) {
  const { t } = useTranslation();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const total = images.length;
  const loop = total > 1;

  // The track carries a clone of the last image before the first one, and a clone of
  // the first after the last: wrapping around then keeps sliding in the same
  // direction instead of rewinding across every photo. Landing on a clone is followed
  // by an instant hop to its real twin — the same image, so nothing flickers.
  const slides = useMemo(
    () => (loop ? [images[total - 1], ...images, images[0]] : images),
    [images, loop, total]
  );
  const firstReal = loop ? 1 : 0;

  const [position, setPosition] = useState(firstReal);
  const [animated, setAnimated] = useState(true);
  const [dragX, setDragX] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const dragging = useRef(false);
  const lastSwipeAt = useRef(0);
  const pendingPosition = useRef<number | null>(null);

  // Where the image on screen really lives on the track: a clone maps to its twin
  const anchor = loop ? (position === 0 ? total : position === total + 1 ? 1 : position) : position;
  const index = anchor - firstReal;

  // Photos can be added or deleted from the modal while the track is running
  useEffect(() => {
    setPosition(p => (p >= 0 && p < slides.length ? p : firstReal));
  }, [slides.length, firstReal]);

  useEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  const moveTo = useCallback(
    (target: number) => {
      if (anchor !== position) {
        // Parked on a clone: hop to the real twin without animating, then move on
        // from there, so the track never runs off its own end.
        pendingPosition.current = target;
        setAnimated(false);
        setPosition(anchor);
        return;
      }
      setPosition(target);
    },
    [anchor, position]
  );

  // Re-enable the animation one frame after an instant hop, applying whatever move
  // was waiting on it — the hop itself must never be painted as a slide.
  useEffect(() => {
    if (animated) return;
    const frame = requestAnimationFrame(() => {
      setAnimated(true);
      const target = pendingPosition.current;
      pendingPosition.current = null;
      if (target !== null) setPosition(target);
    });
    return () => cancelAnimationFrame(frame);
  }, [animated]);

  // Once the slide onto a clone is over, hop to its real twin
  useEffect(() => {
    if (anchor === position) return;
    const timer = window.setTimeout(() => {
      setAnimated(false);
      setPosition(anchor);
    }, SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [anchor, position]);

  // A timeout per slide rather than an interval: any manual move restarts the
  // countdown, so the next auto-advance is never right on the heels of a click.
  useEffect(() => {
    if (!loop || paused || reducedMotion) return;
    const timer = window.setTimeout(() => moveTo(anchor + 1), intervalMs);
    return () => window.clearTimeout(timer);
  }, [loop, paused, reducedMotion, intervalMs, anchor, moveTo]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    dragging.current = false;
    setPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!loop || !touchStart.current) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    // A mostly vertical drag is the user scrolling the page past the cover
    if (!dragging.current && (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < 8)) return;
    dragging.current = true;
    setDragX(dx);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setPaused(false);
    setDragX(0);
    const start = touchStart.current;
    const wasDragging = dragging.current;
    touchStart.current = null;
    dragging.current = false;
    if (!start || !loop) return;

    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      lastSwipeAt.current = Date.now();
      moveTo(anchor + (dx < 0 ? 1 : -1));
    } else if (wasDragging) {
      // Dragged, but not far enough to change photo: still not a tap
      lastSwipeAt.current = Date.now();
    }
  };

  // A swipe still ends in a click on the cover, which would open the full-screen
  // carousel. Swallow that click in the capture phase so it never reaches the cover.
  const handleClickCapture = (e: React.MouseEvent) => {
    if (Date.now() - lastSwipeAt.current < 500) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Arrows and dots must not bubble up to the cover's "open the carousel" click
  const handleControl = (e: React.MouseEvent, target: number) => {
    e.stopPropagation();
    moveTo(target);
  };

  return (
    <div
      className="absolute inset-0 overflow-hidden group/slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClickCapture={handleClickCapture}
    >
      <div
        className="flex h-full w-full"
        style={{
          transform: `translateX(calc(${-position * 100}% + ${dragX}px))`,
          transition: animated && dragX === 0 ? `transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
        }}
      >
        {slides.map((url, i) => (
          <img
            key={`${url}-${i}`}
            src={url}
            alt={i === position ? alt : ''}
            aria-hidden={i !== position}
            draggable={false}
            // Neighbours are fetched ahead of time so a slide never lands on a blank
            loading={Math.abs(i - position) <= 1 ? 'eager' : 'lazy'}
            className="w-full h-full shrink-0 object-cover select-none"
          />
        ))}
      </div>

      {loop && (
        <>
          {/* Manual navigation — discreet until the cover is hovered, always tappable */}
          <button
            type="button"
            aria-label={t('locationImages.previousPhoto')}
            onClick={e => handleControl(e, anchor - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/30 p-1.5 text-white transition-all hover:bg-black/60 opacity-60 group-hover/slideshow:opacity-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={t('locationImages.nextPhoto')}
            onClick={e => handleControl(e, anchor + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/30 p-1.5 text-white transition-all hover:bg-black/60 opacity-60 group-hover/slideshow:opacity-100"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Dots sit bottom-center, between the status badge and the follow actions */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {images.map((url, i) => (
              <button
                key={`dot-${url}-${i}`}
                type="button"
                aria-label={t('locationImages.goToPhoto', { number: i + 1 })}
                aria-current={i === index}
                onClick={e => handleControl(e, firstReal + i)}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-5 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
